// resourceDrag — the typed in-page drag payload that lets a resource row (the
// Library) be dropped onto a composer as an `@`-mention.
//
// The drag source writes a small JSON payload under a custom MIME type; the
// drop target PARSES it back at the boundary (parse, don't validate). Anything
// that does not parse — an OS file drag, a text drag, another app's payload, a
// malformed blob — is ignored. File drops are NOT handled here: they go through
// IngestHost's window listener (upload → source → composer chip, F83).
//
// Pure: no DOM, no React. The component only reads/writes `dataTransfer`.

import type { ComposerResourceKind, ComposerResourceRef } from "./types.js";

// Custom MIME — never `text/plain`, so a drop on a text field can't paste the
// raw JSON, and only targets that know this type react to it.
export const RESOURCE_DRAG_MIME = "application/x-resource-ref+json";

// The wire shape written into `dataTransfer` (named + discriminated).
type ResourceDragPayload = {
    readonly type: "resource_ref";
    readonly kind: ComposerResourceKind;
    readonly id: string;
    readonly title: string;
};

export const encodeResourceDrag = (ref: ComposerResourceRef): string => {
    const payload: ResourceDragPayload = {
        type: "resource_ref",
        kind: ref.kind,
        id: ref.id,
        title: ref.title,
    };
    return JSON.stringify(payload);
};

const parseKind = (value: unknown): ComposerResourceKind | null => {
    switch (value) {
        case "source":
        case "material":
        case "package":
        case "file":
            return value;
        default:
            return null;
    }
};

const parseJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

// The boundary parser: a raw `dataTransfer` string → a mentionable resource,
// or null for anything that isn't a well-formed resource payload.
export const parseResourceDrag = (raw: string): ComposerResourceRef | null => {
    const value = parseJson(raw);
    if (
        typeof value !== "object" ||
        value === null ||
        !("type" in value) ||
        value.type !== "resource_ref" ||
        !("kind" in value) ||
        !("id" in value) ||
        !("title" in value)
    )
        return null;
    const kind = parseKind(value.kind);
    const { id, title } = value;
    if (kind === null || typeof id !== "string" || typeof title !== "string")
        return null;
    if (id === "") return null;
    return { kind, id, title };
};

// Drop → next mention list. Null means "nothing to do": the payload isn't a
// resource, or that resource is already mentioned (a mention is inserted once).
export const mentionsAfterDrop = (
    mentions: readonly ComposerResourceRef[],
    raw: string,
): readonly ComposerResourceRef[] | null => {
    const ref = parseResourceDrag(raw);
    if (ref === null || mentions.some((m) => m.id === ref.id)) return null;
    return [...mentions, ref];
};
